/**
 * The "Other" avatar library — the FULL list of the user's own avatars.
 *
 * Why it exists: My Account shows only the 5 most recent inline. Accounts with
 * 200+ generations must never render 200 thumbnails, so this dialog pages
 * through the database (24 per page, "Load more") and only mounts thumbnails
 * for the pages actually requested. Stored `/cutouts/` URLs render as-is.
 */
import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import ProfileAvatar from "@/components/consumer/ProfileAvatar";
import { listOwnAvatarsPage, type OwnAvatar } from "@/services/profile-display";

const PAGE_SIZE = 24;

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string;
  backgroundColour: string;
  selectedId: string | null;
  /** Selecting from the library uses the SAME persist path as the inline five. */
  onSelect: (avatar: OwnAvatar) => void;
}

export default function AvatarLibraryDialog({
  open,
  onOpenChange,
  userId,
  backgroundColour,
  selectedId,
  onSelect,
}: Props) {
  const [rows, setRows] = useState<OwnAvatar[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);

  // Load the FIRST page only when the dialog is actually opened.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      const page = await listOwnAvatarsPage(userId, 0, PAGE_SIZE);
      if (cancelled) return;
      setRows(page.rows);
      setHasMore(page.hasMore);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [open, userId]);

  async function loadMore() {
    setLoading(true);
    const page = await listOwnAvatarsPage(userId, rows.length, PAGE_SIZE);
    setRows((prev) => [...prev, ...page.rows]);
    setHasMore(page.hasMore);
    setLoading(false);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[80vh] overflow-y-auto sm:max-w-[520px]" data-testid="s9-avatar-library">
        <DialogHeader>
          <DialogTitle>All your avatars</DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-4 gap-3 sm:grid-cols-5" data-testid="s9-avatar-library-grid">
          {rows.map((g) => (
            <button
              key={g.id}
              type="button"
              aria-pressed={selectedId === g.id}
              data-testid={`s9-library-option-${g.id}`}
              onClick={() => {
                onSelect(g);
                onOpenChange(false);
              }}
              className={`h-[62px] w-[62px] overflow-hidden rounded-full border-2 transition-colors ${
                selectedId === g.id ? "border-grape" : "border-transparent hover:border-ink/25"
              }`}
            >
              <ProfileAvatar
                avatarUrl={g.generated_image_url}
                backgroundColour={backgroundColour}
                className="h-full w-full"
              />
            </button>
          ))}
        </div>

        {rows.length === 0 && !loading ? (
          <p className="text-[14px] text-foreground/70">No avatars yet.</p>
        ) : null}

        <div className="flex items-center gap-3 pt-1">
          {hasMore ? (
            <button
              type="button"
              onClick={loadMore}
              disabled={loading}
              data-testid="s9-library-load-more"
              className="flex min-h-11 items-center rounded-full border border-ink/20 px-[18px] py-2.5 text-[14px] font-semibold text-ink transition-opacity disabled:opacity-40"
            >
              {loading ? "Loading…" : "Load more"}
            </button>
          ) : null}
          <span className="text-[13px] text-foreground/60" data-testid="s9-library-count">
            Showing {rows.length}
          </span>
        </div>
      </DialogContent>
    </Dialog>
  );
}
